import React, { useState, useEffect, useRef } from 'react';
import { upload } from '@vercel/blob/client';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import UserSelector from './UserSelector';
import {
  normalizeVideos,
  parseVideoUrl,
  MAX_SONG_VIDEOS,
  MAX_VIDEO_NOTES_LENGTH
} from '../utils/songVideos';
import '../styles/metadata-edit-dialog.css';

const MetadataEditDialog = ({ isOpen, onClose, file, onSave, onPdfUploaded }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [defaultTempoBPM, setDefaultTempoBPM] = useState('');
  const [restrictedUsers, setRestrictedUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && file) {
      setPdfUrl(file.pdfUrl || null);
      loadMetadata();
    }
  }, [isOpen, file]);

  const loadMetadata = async () => {
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/musicxml?operation=get-metadata&filename=${encodeURIComponent(file.filename)}`
      );

      if (response.ok) {
        const metadata = await response.json();
        setTitle(metadata.title || '');
        setDefaultTempoBPM(metadata.defaultTempoBPM || '');
        setRestrictedUsers(metadata.restrictedUsers || []);
        setVideos(Array.isArray(metadata.videos) ? metadata.videos : []);
      } else {
        console.error('Failed to load metadata');
      }
    } catch (error) {
      console.error('Error loading metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const addVideo = () => {
    setVideos(prev => [...prev, { url: '', title: '', notes: '' }]);
  };

  const removeVideo = (index) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  };

  const updateVideo = (index, field, value) => {
    setVideos(prev => prev.map((video, i) => (
      i === index ? { ...video, [field]: value } : video
    )));
  };

  // Official PDFs upload straight from the browser to blob storage (they can
  // exceed the serverless request size limit), authorized by a server token
  const handlePdfSelected = async (event) => {
    const pdfFile = event.target.files[0];
    event.target.value = '';
    if (!pdfFile) return;

    if (!pdfFile.name.toLowerCase().endsWith('.pdf')) {
      alert(t('storage.selectPdf'));
      return;
    }

    setUploadingPdf(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const blob = await upload(`musicxml/${file.filename}.pdf`, pdfFile, {
        access: 'public',
        contentType: 'application/pdf',
        handleUploadUrl: `${API_BASE}/api/musicxml?operation=upload-pdf`,
      });

      // Overwrites keep the same URL; version it so caches serve the new file
      const versionedUrl = `${blob.url}${blob.url.includes('?') ? '&' : '?'}v=${Date.now()}`;
      setPdfUrl(versionedUrl);
      if (onPdfUploaded) {
        onPdfUploaded(file.filename, versionedUrl);
      }
      alert(t('storage.pdfUploadSuccess'));
    } catch (error) {
      console.error('PDF upload error:', error);
      alert(t('storage.uploadFailed', { error: error.message }));
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/musicxml?operation=update-metadata&filename=${encodeURIComponent(file.filename)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: title || null,
            defaultTempoBPM: defaultTempoBPM ? parseInt(defaultTempoBPM, 10) : null,
            restrictedUsers: restrictedUsers || [],
            // Strips blank rows the admin added but never filled in
            videos: normalizeVideos(videos),
            userId: user.id,
            userRole: user.role,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(t('metadata.saveSuccess'));
        if (onSave) {
          onSave(file.filename, result.metadata);
        }
        onClose();
      } else {
        alert(t('metadata.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving metadata:', error);
      alert(t('metadata.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="metadata-edit-overlay">
      <div className="metadata-edit-dialog">
        <div className="metadata-edit-header">
          <h2>{t('metadata.editTitle', { filename: file?.filename })}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="metadata-edit-content">
          {loading ? (
            <div className="loading">{t('metadata.loading')}</div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="title">{t('metadata.titleLabel')}</label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('metadata.titlePlaceholder')}
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tempo">{t('metadata.tempoLabel')}</label>
                <input
                  type="number"
                  id="tempo"
                  value={defaultTempoBPM}
                  onChange={(e) => setDefaultTempoBPM(e.target.value)}
                  placeholder={t('metadata.tempoPlaceholder')}
                  min="20"
                  max="300"
                  disabled={saving}
                />
                <div className="field-hint">{t('metadata.tempoHint')}</div>
              </div>

              <div className="form-group">
                <UserSelector
                  selectedUsers={restrictedUsers}
                  onSelectionChange={setRestrictedUsers}
                  disabled={saving}
                />
                <div className="field-hint">{t('metadata.restrictedUsersHint')}</div>
              </div>

              <div className="form-group">
                <label>{t('metadata.pdfLabel')}</label>
                <div className="pdf-attachment-row">
                  {pdfUrl ? (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pdf-current-link"
                    >
                      📄 {t('metadata.pdfCurrent')}
                    </a>
                  ) : (
                    <span className="pdf-none">{t('metadata.pdfNone')}</span>
                  )}
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfSelected}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="pdf-upload-button"
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={saving || uploadingPdf}
                  >
                    {uploadingPdf
                      ? `⏳ ${t('storage.uploading')}`
                      : (pdfUrl ? t('storage.replacePdf') : t('storage.uploadPdf'))}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>{t('metadata.videosLabel')}</label>
                <div className="field-hint">{t('metadata.videosHint')}</div>

                {videos.map((video, index) => {
                  const unrecognized = video.url.trim() && !parseVideoUrl(video.url).provider;
                  return (
                    <div className="video-entry" key={index}>
                      <div className="video-entry-header">
                        <span className="video-entry-number">#{index + 1}</span>
                        <button
                          type="button"
                          className="video-remove-button"
                          onClick={() => removeVideo(index)}
                          disabled={saving}
                          title={t('metadata.videoRemove')}
                          aria-label={t('metadata.videoRemove')}
                        >
                          ×
                        </button>
                      </div>

                      <input
                        type="text"
                        value={video.url}
                        onChange={(e) => updateVideo(index, 'url', e.target.value)}
                        placeholder={t('metadata.videoUrlPlaceholder')}
                        disabled={saving}
                      />
                      {unrecognized && (
                        <div className="video-entry-warning">
                          {t('metadata.videoUrlUnrecognized')}
                        </div>
                      )}

                      <input
                        type="text"
                        value={video.title}
                        onChange={(e) => updateVideo(index, 'title', e.target.value)}
                        placeholder={t('metadata.videoTitlePlaceholder')}
                        disabled={saving}
                      />

                      <textarea
                        value={video.notes}
                        onChange={(e) => updateVideo(index, 'notes', e.target.value)}
                        placeholder={t('metadata.videoNotesPlaceholder')}
                        maxLength={MAX_VIDEO_NOTES_LENGTH}
                        rows={3}
                        disabled={saving}
                      />
                    </div>
                  );
                })}

                {videos.length < MAX_SONG_VIDEOS && (
                  <button
                    type="button"
                    className="add-video-button"
                    onClick={addVideo}
                    disabled={saving}
                  >
                    + {t('metadata.videoAdd')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="metadata-edit-footer">
          <button className="cancel-button" onClick={onClose} disabled={saving}>
            {t('controls.cancel')}
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? t('metadata.saving') : t('controls.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetadataEditDialog;